import { displaySize } from "../../constants.js";

/**
 * Parametric per-size layout geometry for the templates this app actually
 * exposes. Never mutate a referenced template version — a real change
 * would need a new version, same rule as the original.
 */

// Largest sizes the Composer currently offers (1200x630 landscape,
// 1080x1920 story). Font sizes and logo boxes below were originally tuned
// as fixed/binary-threshold values around a ~300px reference and never
// extended past display-ad dimensions - correct on a 300x250, but tiny
// and disproportionate on a 1080-wide or 1920-tall canvas. scaleCap grows
// them proportionally instead, capped at whatever the same ratio gives at
// our largest real size, so a value can't run away even if a much bigger
// bigger size gets added to constants.js later without this file being revisited.
const MAX_W = 1200;
const MAX_H = 1920;
const REF = 300; // the dimension the original fixed values were tuned for

/** Scales basePx proportionally to `dim`, capped at its value at `capDim`. */
function scaleCap(basePx, dim, capDim) {
  return Math.min(Math.round(basePx * (dim / REF)), Math.round(basePx * (capDim / REF)));
}

/** Same, with a floor so small sizes never shrink below the original minimum. */
function scaleFont(basePx, dim, minPx, capDim = MAX_W) {
  return Math.max(minPx, scaleCap(basePx, dim, capDim));
}

/**
 * For logo/element sizes driven by ch (content height): ch alone runs away
 * on tall-narrow formats (160x600) where ch is large despite the canvas
 * itself being small. Also caps by a fraction of width, so an element can
 * never dominate a narrow canvas just because it's tall.
 */
function scaleCapWH(basePx, ch, w, capH = MAX_H, wRatio = 0.35) {
  return Math.min(scaleCap(basePx, ch, capH), Math.round(w * wRatio));
}

export function complianceStrip(w, h) {
  const stripH = h <= 100 ? 11 : 14;
  return { x: 0, y: h - stripH, w, h: stripH, fontSize: h <= 100 ? 7 : 9, align: "center" };
}

/** Wide-and-short formats lay out as a single horizontal row. */
function isHorizontal(w, h) {
  return w >= h * 3;
}

// ---- logo-lockup: logo + headline + CTA, no image; all sizes ----

function logoLockupLayout(w, h) {
  const compliance = complianceStrip(w, h);
  const ch = compliance.y;
  const pad = h <= 100 ? 6 : 12;

  if (isHorizontal(w, h)) {
    const logoSide = ch - pad * 2;
    const ctaW = Math.min(110, Math.round(w * 0.2));
    const ctaH = Math.min(30, ch - pad * 2);
    return {
      logo: { x: pad, y: pad, w: logoSide, h: logoSide },
      headline: {
        x: pad + logoSide + 10,
        y: pad,
        w: w - (pad + logoSide + 10) - (ctaW + pad + 10),
        h: ch - pad * 2,
        fontSize: Math.min(18, Math.round(ch * 0.42)),
        fontWeight: 700,
        align: "left",
      },
      cta: {
        x: w - ctaW - pad,
        y: Math.round((ch - ctaH) / 2),
        w: ctaW,
        h: ctaH,
        fontSize: Math.min(13, Math.round(ctaH * 0.45)),
      },
      compliance,
    };
  }

  const hasSubline = ch >= 220;
  const logoSide = Math.min(scaleCapWH(56, ch, w), Math.round(ch * 0.18));
  const headlineY = pad + logoSide + Math.round(ch * 0.06);
  const headlineH = Math.round(ch * (hasSubline ? 0.3 : 0.38));
  const sublineH = Math.round(ch * 0.16);
  const ctaH = 34;
  return {
    logo: { x: Math.round((w - logoSide) / 2), y: pad, w: logoSide, h: logoSide },
    headline: {
      x: pad,
      y: headlineY,
      w: w - pad * 2,
      h: headlineH,
      fontSize: scaleFont(22, w, 16, MAX_W),
      fontWeight: 700,
      align: "center",
    },
    ...(hasSubline
      ? {
          subline: {
            x: pad,
            y: headlineY + headlineH + 4,
            w: w - pad * 2,
            h: sublineH,
            fontSize: scaleFont(13, w, 11, MAX_W),
            align: "center",
          },
        }
      : {}),
    cta: {
      x: Math.round(w * 0.18),
      y: ch - ctaH - pad,
      w: Math.round(w * 0.64),
      h: ctaH,
      fontSize: scaleFont(13, w, 11, MAX_W),
    },
    compliance,
  };
}

// ---- full-bleed: image background + scrim + overlaid copy ----

function fullBleedLayout(w, h) {
  const compliance = complianceStrip(w, h);
  const ch = compliance.y;
  const pad = 12;
  const narrow = w < 220; // 160x600
  const logoSide = Math.min(scaleCapWH(40, ch, w), Math.round(ch * 0.14));
  const hasSubline = ch >= 260;
  const ctaH = 32;
  const headlineH = Math.round(ch * (hasSubline ? 0.22 : 0.28));
  const headlineY =
    ch - ctaH - pad * 2 - (hasSubline ? Math.round(ch * 0.14) + 4 : 0) - headlineH;
  return {
    imageAsBackground: true,
    scrim: true,
    logo: { x: pad, y: pad, w: logoSide, h: logoSide },
    headline: {
      x: pad,
      y: headlineY,
      w: w - pad * 2,
      h: headlineH,
      fontSize: scaleFont(20, w, narrow ? 14 : 16, MAX_W),
      fontWeight: 700,
      align: "left",
    },
    ...(hasSubline
      ? {
          subline: {
            x: pad,
            y: headlineY + headlineH + 4,
            w: w - pad * 2,
            h: Math.round(ch * 0.14),
            fontSize: scaleFont(12, w, 10, MAX_W),
            align: "left",
          },
        }
      : {}),
    cta: {
      x: pad,
      y: ch - ctaH - pad,
      w: Math.min(150, w - pad * 2),
      h: ctaH,
      fontSize: scaleFont(13, w, 11, MAX_W),
    },
    compliance,
  };
}

// ---- custom-image: bring-your-own creative, ALL sizes, no scrim ----
// The uploaded image IS the banner; text defaults to baked. Only a small
// logo, optional CTA, and the compliance strip are reserved. No gradient
// scrim — the creative shows exactly as uploaded.

function customImageLayout(w, h) {
  const compliance = complianceStrip(w, h);
  const ch = compliance.y;
  const pad = h <= 100 ? 5 : 10;
  const logoSide = Math.max(10, Math.min(scaleCapWH(40, ch, w), Math.round(ch * 0.2)));
  const ctaH = Math.max(14, Math.min(scaleCapWH(30, ch, w, MAX_H, 0.5), Math.round(ch * 0.26)));
  const ctaW = Math.max(48, Math.min(scaleCap(140, w, MAX_W), Math.round(w * 0.34)));
  const headlineH = Math.max(10, Math.round(ch * 0.22));
  const headlineY = Math.min(pad + logoSide + 6, ch - ctaH - headlineH - pad);
  return {
    imageAsBackground: true,
    logo: { x: pad, y: pad, w: logoSide, h: logoSide },
    headline: {
      x: pad,
      y: Math.max(pad, headlineY),
      w: w - pad * 2,
      h: headlineH,
      fontSize: scaleFont(20, w, 13, MAX_W),
      fontWeight: 700,
      align: "left",
    },
    cta: {
      x: pad,
      y: ch - ctaH - pad,
      w: ctaW,
      h: ctaH,
      fontSize: Math.max(10, Math.min(scaleCap(13, ch, MAX_H), Math.round(ctaH * 0.42))),
    },
    compliance,
  };
}

// ---- assembly ----

function buildLayouts(sizes, fn) {
  const layout = {};
  for (const id of sizes) {
    const size = displaySize(id);
    if (!size) throw new Error(`Unknown display size: ${id}`);
    layout[id] = fn(size.w, size.h);
  }
  return layout;
}

const LAYOUT_FN_BY_TEMPLATE_ID = {
  "logo-lockup": logoLockupLayout,
  "full-bleed": fullBleedLayout,
  "custom-image": customImageLayout,
};

/** All per-size layouts for a template, across the given sizes. */
export function layoutsForTemplate(templateId, sizes) {
  const fn = LAYOUT_FN_BY_TEMPLATE_ID[templateId];
  if (!fn) throw new Error(`Unknown or unsupported template: ${templateId}`);
  return buildLayouts(sizes, fn);
}

/** The layout for one template at one size. */
export function layoutFor(templateId, sizeId) {
  const size = displaySize(sizeId);
  if (!size) throw new Error(`Unknown display size: ${sizeId}`);
  const fn = LAYOUT_FN_BY_TEMPLATE_ID[templateId];
  if (!fn) throw new Error(`Unknown or unsupported template: ${templateId}`);
  return fn(size.w, size.h);
}